import { useState, useEffect } from "react";
import { Brain, Play, Activity, BarChart3, RefreshCw, Shield, TrendingUp, AlertTriangle, Database } from "lucide-react";
import { API_BASE } from "@/lib/api";

interface StrategyResult {
  id: string;
  story: string;
  is_sharpe: number;
  oos_sharpe: number;
  max_dd: number;
  profit_factor: number;
}

export default function AIQuantPage() {
  const [loading, setLoading] = useState(false);
  const [strategies, setStrategies] = useState<StrategyResult[]>([]);
  const [status, setStatus] = useState<any>(null);
  const [features, setFeatures] = useState<any>(null);
  const [lstmModels, setLstmModels] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState("research");

  useEffect(() => {
    fetchStatus();
    fetchFeatures();
    fetchLSTMStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      const r = await fetch(`${API_BASE}/api/ai-quant/status`);
      const data = await r.json();
      setStatus(data);
    } catch (e) { console.error(e); }
  };

  const fetchFeatures = async () => {
    try {
      const r = await fetch(`${API_BASE}/api/ai-quant/features`);
      const data = await r.json();
      setFeatures(data);
    } catch (e) { console.error(e); }
  };

  const fetchLSTMStatus = async () => {
    try {
      const r = await fetch(`${API_BASE}/api/ai-quant/lstm/status`);
      const data = await r.json();
      setLstmModels(data.models || []);
    } catch (e) { console.error(e); }
  };

  const runCycle = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_BASE}/api/ai-quant/run-cycle`, { method: "POST" });
      const data = await r.json();
      alert(`Cycle: ${data.accepted ? "ACCEPTED" : "REJECTED"} | ${data.issues?.join(", ") || "No issues"}`);
      fetchStatus();
    } catch (e) { alert("Error: " + e); }
    setLoading(false);
  };

  const runLoop = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_BASE}/api/ai-quant/run-loop?iterations=15&target=3`, { method: "POST" });
      const data = await r.json();
      setStrategies(data.strategies || []);
      alert(`Loop complete: ${data.strategies_found} strategies found`);
    } catch (e) { alert("Error: " + e); }
    setLoading(false);
  };

  const trainLSTM = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_BASE}/api/ai-quant/lstm/train`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ symbol: "BTC-5MIN", epochs: 30, lookback: 20 }),
      });
      const data = await r.json();
      alert(`LSTM: ${data.status} | Accuracy: ${(data.test_accuracy * 100).toFixed(1)}%`);
      fetchLSTMStatus();
    } catch (e) { alert("Error: " + e); }
    setLoading(false);
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-white flex items-center gap-2">
            <Brain size={18} className="text-terminal-accent" />
            AI QUANT RESEARCH LAB
          </h1>
          <p className="text-xs text-gray-500 mt-1">6-agent research pipeline + LSTM neural networks</p>
        </div>
        <div className="flex gap-2">
          {["research", "lstm", "features"].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-3 py-1 text-xs border ${
                activeTab === tab ? "border-terminal-accent text-terminal-accent bg-terminal-accent/10" : "border-gray-700 text-gray-500"
              }`}
            >
              {tab.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      {/* Status Bar */}
      {status && (
        <div className="grid grid-cols-4 gap-4">
          <StatCard label="ATTEMPTS" value={status.total_attempts} icon={Activity} />
          <StatCard label="ACCEPTED" value={status.accepted_strategies} icon={Shield} color="text-green-400" />
          <StatCard label="DSR THRESHOLD" value={status.deflated_threshold?.toFixed(2)} icon={AlertTriangle} />
          <StatCard label="DATA ROWS" value={status.data_rows} icon={Database} />
        </div>
      )}

      {/* RESEARCH TAB */}
      {activeTab === "research" && (
        <div className="space-y-4">
          <div className="terminal-window p-4">
            <div className="flex gap-4">
              <button onClick={runCycle} disabled={loading} className="terminal-btn-primary flex items-center gap-2 text-xs">
                {loading ? <RefreshCw size={14} className="animate-spin" /> : <Play size={14} />}
                RUN SINGLE CYCLE
              </button>
              <button onClick={runLoop} disabled={loading} className="terminal-btn-primary flex items-center gap-2 text-xs">
                {loading ? <RefreshCw size={14} className="animate-spin" /> : <Brain size={14} />}
                RUN FULL LOOP (15 iters)
              </button>
              <button onClick={() => { fetchStatus(); }} className="terminal-btn-secondary text-xs">
                <RefreshCw size={14} /> REFRESH
              </button>
            </div>
            <p className="text-[10px] text-gray-500 mt-2">
              3 hard gates: Critic review + Deflated Sharpe (DSR &gt; 0.95) + Walk-forward validation
            </p>
          </div>

          {/* Strategies Table */}
          {strategies.length > 0 && (
            <div className="terminal-window">
              <div className="terminal-header">
                <span className="flex items-center gap-2"><TrendingUp size={12} /> ACCEPTED STRATEGIES</span>
                <span className="text-green-400">{strategies.length} passed</span>
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-terminal-border text-gray-500 text-xs">
                    <th className="text-left px-2 py-2">ID</th>
                    <th className="text-left px-2 py-2">STORY</th>
                    <th className="text-right px-2 py-2">IS SHARPE</th>
                    <th className="text-right px-2 py-2">OOS SHARPE</th>
                  </tr>
                </thead>
                <tbody>
                  {strategies.map((s) => (
                    <tr key={s.id} className="border-b border-terminal-border/50 hover:bg-white/5">
                      <td className="px-2 py-1.5 text-xs text-terminal-accent">{s.id}</td>
                      <td className="px-2 py-1.5 text-xs">{s.story}</td>
                      <td className="px-2 py-1.5 text-right text-xs">{s.is_sharpe?.toFixed(2)}</td>
                      <td className="px-2 py-1.5 text-right text-xs">{s.oos_sharpe?.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* LSTM TAB */}
      {activeTab === "lstm" && (
        <div className="space-y-4">
          <div className="terminal-window p-4">
            <div className="flex gap-4 items-center">
              <button onClick={trainLSTM} disabled={loading} className="terminal-btn-primary flex items-center gap-2 text-xs">
                {loading ? <RefreshCw size={14} className="animate-spin" /> : <Brain size={14} />}
                TRAIN LSTM
              </button>
              <div className="text-[10px] text-gray-500">
                Lookback: 20 bars | Hidden: 64 | Epochs: 30 | Threshold: 0.52
              </div>
            </div>
            <p className="text-[10px] text-gray-500 mt-2">
              LSTM learns conditional expectation E[Y|X] from stationary features.
              Expected directional accuracy: 52-57% for production models.
            </p>
          </div>

          {/* Models */}
          <div className="terminal-window">
            <div className="terminal-header">
              <span className="flex items-center gap-2"><BarChart3 size={12} /> SAVED MODELS</span>
            </div>
            {lstmModels.length === 0 ? (
              <div className="p-4 text-sm text-gray-500 text-center">No LSTM models trained yet</div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-terminal-border text-gray-500 text-xs">
                    <th className="text-left px-2 py-2">SYMBOL</th>
                    <th className="text-right px-2 py-2">SIZE</th>
                  </tr>
                </thead>
                <tbody>
                  {lstmModels.map((m) => (
                    <tr key={m.symbol} className="border-b border-terminal-border/50">
                      <td className="px-2 py-1.5 text-xs text-terminal-accent">{m.symbol}</td>
                      <td className="px-2 py-1.5 text-right text-xs">{m.size_mb} MB</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* FEATURES TAB */}
      {activeTab === "features" && features && (
        <div className="terminal-window">
          <div className="terminal-header">
            <span className="flex items-center gap-2"><BarChart3 size={12} /> STATIONARY FEATURES</span>
            <span className="text-gray-500">{features.feature_count} features</span>
          </div>
          <div className="p-4 space-y-2">
            {features.features?.map((f: string) => (
              <div key={f} className="flex justify-between items-center border-b border-terminal-border/30 py-1">
                <span className="text-xs text-gray-400">{f}</span>
                <span className="text-xs text-terminal-accent font-mono">
                  {features.latest_values?.[f]?.toFixed(4) || "N/A"}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, icon: Icon, color = "text-white" }: any) {
  return (
    <div className="terminal-window p-3">
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] text-gray-500">{label}</span>
        <Icon size={12} className={color} />
      </div>
      <div className={`text-lg font-bold ${color}`}>{value ?? "-"}</div>
    </div>
  );
}
